import CheckBox from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlank from '@mui/icons-material/CheckBoxOutlineBlank';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemButton,
  Radio,
  Typography,
} from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import { PickingStationEntity } from '@peritasai/repository';
import { Typography as UITypography } from '@peritasai/ui-components';
import { periverseTheme, themeShape } from '@peritasai/ui-shared';
import React from 'react';
import { PickingStationStatusChip } from './PickingStationStatusChip';
import {
  DialogCloseButton,
  WarningIcon,
  WarningIconAvatar,
  WarningIconContainer,
} from './dialogShared';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: themeShape.borderRadius.medium,
    maxWidth: '600px',
    width: '100%',
  },
}));

const StationListItem = styled(ListItem)({
  padding: 0,
});

const StationListItemReadOnly = styled(ListItem)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 16,
}));

const StationRow = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected?: boolean }>(({ theme, selected }) => {
  const selectedBgColor = alpha(theme.palette.primary.main, 0.08);
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1,
    borderRadius: themeShape.borderRadius.medium,

    backgroundColor: selected ? selectedBgColor : 'transparent',
    '&:hover': {
      backgroundColor: selectedBgColor,
    },
    transition: 'background-color 0.2s ease',
  };
});

const StationInfo = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  flex: 1,
});

const StationText = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
});

const StationName = styled(UITypography)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightMedium,
  fontSize: '1rem',
}));

interface PickingStationSelectionDialogProps {
  open: boolean;
  stations: PickingStationEntity[];
  selectedStationId?: string | null;
  onSelectedStationChange?: (stationId: string | null) => void;
  onSelect?: (stationId: string) => void;
  onCancel: () => void;
}

export const PickingStationSelectionDialog: React.FC<
  PickingStationSelectionDialogProps
> = ({
  open,
  stations,
  selectedStationId = null,
  onSelectedStationChange,
  onSelect,
  onCancel,
}) => {
  const hasAvailableStations = stations.some(
    (station) => station.status === 'Available'
  );

  const handleSelect = () => {
    if (selectedStationId && onSelect) {
      onSelect(selectedStationId);
    }
  };

  const handleCancel = () => {
    onCancel();
  };

  const handleClose = () => {
    onCancel();
  };

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      data-testid="picking-station-selection-dialog"
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: hasAvailableStations ? 3 : 2,
          borderBottom: '1px solid rgba(0,0,0,0.12)',
        }}
      >
        {hasAvailableStations ? (
          <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
            Select Picking Station
          </Typography>
        ) : (
          <WarningIconContainer>
            <WarningIconAvatar>
              <WarningIcon />
            </WarningIconAvatar>
            <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
              Picking Stations Unavailable
            </Typography>
          </WarningIconContainer>
        )}
        <DialogCloseButton onClick={handleClose} />
      </DialogTitle>
      <DialogContent sx={{ padding: hasAvailableStations ? 0 : 2 }}>
        {hasAvailableStations ? (
          <>
            <Box sx={{ paddingTop: 3, paddingBottom: 3, paddingX: 2.5 }}>
              <UITypography
                variant="body1"
                sx={{ paddingLeft: 0.5, color: 'text.secondary' }}
              >
                Select an available Picking Station to begin:
              </UITypography>
              <List sx={{ padding: 0 }}>
                {stations.map((station) => {
                  const isSelected = selectedStationId === station.id;
                  const isAvailable = station.status === 'Available';
                  const stationSubtextParts: string[] = [];
                  if (
                    station.locationDisplayName &&
                    station.locationDisplayName !== station.name
                  ) {
                    stationSubtextParts.push(station.locationDisplayName);
                  }
                  if (station.assetLifecycleFunction) {
                    stationSubtextParts.push(station.assetLifecycleFunction);
                  }
                  const stationSubtext = stationSubtextParts.join(' • ');

                  return (
                    <StationListItem key={station.id}>
                      <StationRow
                        selected={isSelected}
                        onClick={() => {
                          if (isAvailable && onSelectedStationChange) {
                            onSelectedStationChange(station.id);
                          }
                        }}
                        disabled={!isAvailable}
                        data-testid={`picking-station-row-${station.id}`}
                        sx={{
                          cursor: isAvailable ? 'pointer' : 'not-allowed',
                        }}
                      >
                        <StationInfo>
                          <StationText>
                            <StationName variant="body1">
                              {station.name}
                            </StationName>
                            {stationSubtext ? (
                              <UITypography
                                variant="body2"
                                sx={{ color: 'text.secondary' }}
                              >
                                {stationSubtext}
                              </UITypography>
                            ) : null}
                          </StationText>
                          <PickingStationStatusChip status={station.status} />
                          <Radio
                            checked={isSelected}
                            disabled={!isAvailable}
                            readOnly
                            icon={<CheckBoxOutlineBlank />}
                            checkedIcon={<CheckBox />}
                            sx={{
                              color: 'primary.main',
                              marginLeft: 'auto',
                              '&.Mui-checked': {
                                color: 'primary.main',
                              },
                              pointerEvents: 'none',
                            }}
                          />
                        </StationInfo>
                      </StationRow>
                    </StationListItem>
                  );
                })}
              </List>
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 2,
                marginTop: 3,
                paddingX: 2,
                paddingBottom: 2,
              }}
            >
              <Button
                variant="outlined"
                sx={{ textTransform: 'none' }}
                onClick={handleCancel}
                data-testid="picking-station-cancel-button"
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                sx={{
                  textTransform: 'none',
                  backgroundColor: periverseTheme.palette.primary.main,
                }}
                onClick={handleSelect}
                disabled={!selectedStationId}
                data-testid="picking-station-select-button"
              >
                Select
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Box sx={{ marginTop: 3, marginBottom: 3, paddingX: 0.5 }}>
              <UITypography variant="body1" sx={{ color: 'text.secondary' }}>
                You cannot edit this case as all Picking Stations are busy or
                offline. If a Picking Station is offline, then check it is
                powered on and connected, then try again.
              </UITypography>
            </Box>
            <List sx={{ paddingBottom: 3, marginBottom: 3 }}>
              {stations.map((station) => (
                <StationListItemReadOnly key={station.id}>
                  <StationName
                    variant="body1"
                    sx={{ color: periverseTheme.palette.text.disabled }}
                  >
                    {station.name}
                  </StationName>
                  <PickingStationStatusChip status={station.status} />
                </StationListItemReadOnly>
              ))}
            </List>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginTop: 2,
              }}
            >
              <Button
                sx={{ textTransform: 'none' }}
                variant="contained"
                onClick={handleCancel}
                data-testid="picking-station-cancel-button"
              >
                Cancel
              </Button>
            </Box>
          </>
        )}
      </DialogContent>
    </StyledDialog>
  );
};
